import clsx from 'clsx';
import { FadeIn } from 'components/common/FadeIn';
import EditableLotteryEntry from 'enter2026late/EditableLotteryEntry';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { createRef, useEffect, useRef, useState } from 'react';

import Callout from 'components/common/CalloutAndToast/Callout';
import { useLotteryEntryPickerSlidingModal } from '../LotteryEntryPickerSlidingModal';
import styles from './V2EntriesPicker.module.css';

const stackTransforms: Record<
  number,
  {
    stack1: string;
    stack2?: string;
    stack3?: string;
    containerHeight: number;
    entryHeight: number;
  }
> = {
  1: {
    stack1: '0px',
    containerHeight: 0,
    entryHeight: 0,
  },
  2: {
    stack1: '-130px',
    containerHeight: 12,
    entryHeight: 10,
  },
  3: {
    stack1: '-136px',
    stack2: '-257px',
    containerHeight: 18,
    entryHeight: 16,
  },
  4: {
    stack1: '-142px',
    stack2: '-263px',
    stack3: '-385px',
    containerHeight: 24,
    entryHeight: 22,
  },
};

const stackTransformsOnSummarise: Record<
  number,
  {
    stack1: string;
    stack2?: string;
    stack3?: string;
    containerHeight: number;
    entryHeight: number;
  }
> = {
  1: {
    stack1: '0px',
    containerHeight: 0,
    entryHeight: 0,
  },
  2: {
    stack1: '-130px',
    containerHeight: 62,
    entryHeight: 60,
  },
  3: {
    stack1: '-190px',
    stack2: '-261px',
    containerHeight: 120,
    entryHeight: 118,
  },
  4: {
    stack1: '-245px',
    stack2: '-316px',
    stack3: '-387px',
    containerHeight: 176,
    entryHeight: 174,
  },
};

interface V2EntriesPickerProps {
  isStuck?: boolean;
  warnUser?: boolean;
}

const V2EntriesPicker = observer(
  ({ isStuck, warnUser }: V2EntriesPickerProps) => {
    const enterStore = useEnterStore();
    const entriesContainerRef = useRef<HTMLDivElement>(null);
    const firstEntryRef = useRef<HTMLDivElement>(null);
    // Preserve each slot's ref when entries are added or removed.
    const { current: entryRefs } = useRef<
      ReturnType<typeof createRef<HTMLDivElement>>[]
    >([]);
    enterStore.lines.forEach((_, index) => {
      entryRefs[index] ??= createRef<HTMLDivElement>();
    });

    const stack1Transform = '--transform--stack1';
    const stack2Transform = '--transform--stack2';
    const stack3Transform = '--transform--stack3';

    const [summariseEntries, setSummariseEntires] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [containerMaxHeight, setContainerMaxHeight] = useState<number | null>(
      null,
    );
    const [entryBaseHeight, setEntryBaseHeight] = useState<number | null>(null);
    const [openCallout, setOpenCallout] = useState(false);
    const [showPulse, setShowPulse] = useState(true);

    const calloutText = enterStore.validateText();
    const containerPadding = 16 + 8;

    const incompleteLineIndex = enterStore.lines.findIndex(
      (line) => line.main.includes(null) || line.bonus.includes(null),
    );
    const firstInvalidLineIndex =
      incompleteLineIndex === -1 && calloutText ? 0 : incompleteLineIndex;

    useEffect(() => {
      if (firstInvalidLineIndex === -1) {
        setOpenCallout(false);
        return;
      }

      if (!warnUser) return;

      entryRefs[firstInvalidLineIndex]?.current?.scrollIntoView({
        behavior: 'smooth',
      });

      setShowPulse(false);
      setOpenCallout(true);
    }, [warnUser, firstInvalidLineIndex, entryRefs]);

    useEffect(() => {
      if (showPulse) return;

      const timeout = setTimeout(() => setShowPulse(true), 10);
      return () => clearTimeout(timeout);
    }, [showPulse]);

    useEffect(() => {
      const singleEntry = firstEntryRef.current;
      if (singleEntry) {
        setEntryBaseHeight(singleEntry.offsetHeight);
      }
    }, [firstEntryRef.current]);

    useEffect(() => {
      const ref = entriesContainerRef.current;
      const singleEntry = firstEntryRef.current;

      if (
        !(ref && singleEntry && entryBaseHeight) ||
        (summariseEntries && isStuck)
      ) {
        return;
      }

      const stackTransform =
        stackTransforms[
          enterStore.lines.length as keyof typeof stackTransforms
        ];

      if (isStuck && enterStore.lines.length === 1) {
        // set container height before transition
        setContainerMaxHeight(ref.offsetHeight);
        ref.style.height = `${entryBaseHeight + containerPadding + stackTransform?.containerHeight}px`;

        // set height of single entry to original height
        singleEntry.style.height = `${entryBaseHeight}px`;
        return;
      }

      if (isStuck && enterStore.lines.length > 1) {
        // set container height before transition
        setContainerMaxHeight(ref.offsetHeight);
        ref.style.height = `${ref.offsetHeight}px`;

        setTimeout(() => {
          setCollapsed(true);

          // set new container height
          ref.style.height = `${entryBaseHeight + containerPadding + stackTransform?.containerHeight}px`;
          // set height of single entry where collapsed entries sit
          singleEntry.style.height = `${entryBaseHeight + stackTransform.entryHeight}px`;

          // set stack transformations
          ref.style.setProperty(stack1Transform, stackTransform?.stack1 ?? '');
          ref.style.setProperty(stack2Transform, stackTransform?.stack2 ?? '');
          ref.style.setProperty(stack3Transform, stackTransform?.stack3 ?? '');
        }, 0);
      } else {
        setSummariseEntires(false);
        // return height of container to auto but set old height so it transitions first
        ref.style.height = `${containerMaxHeight}px`;
        ref.style.height = 'auto';

        setCollapsed(false);
        // return height of single entry
        singleEntry.style.height = `${entryBaseHeight}px`;
      }
    }, [isStuck, enterStore.lines.length]);

    useEffect(() => {
      const ref = entriesContainerRef.current;
      const singleEntry = firstEntryRef.current;

      if (!(ref && singleEntry && entryBaseHeight)) {
        return;
      }

      if (!summariseEntries && !isStuck) return;

      const stackTransformSummarise =
        stackTransformsOnSummarise[
          enterStore.lines.length as keyof typeof stackTransformsOnSummarise
        ];

      const stackTransform =
        stackTransforms[
          enterStore.lines.length as keyof typeof stackTransforms
        ];

      if (summariseEntries) {
        // set new container height
        ref.style.height = `${entryBaseHeight + containerPadding + stackTransformSummarise?.containerHeight}px`;
        // set height of single entry where collapsed entries sit
        singleEntry.style.height = `${entryBaseHeight + stackTransformSummarise.entryHeight}px`;

        // set stack transformations
        ref.style.setProperty(
          stack1Transform,
          stackTransformSummarise?.stack1 ?? '',
        );
        ref.style.setProperty(
          stack2Transform,
          stackTransformSummarise?.stack2 ?? '',
        );
        ref.style.setProperty(
          stack3Transform,
          stackTransformSummarise?.stack3 ?? '',
        );
      }

      if (!summariseEntries) {
        // set new container height
        ref.style.height = `${entryBaseHeight + containerPadding + stackTransform?.containerHeight}px`;
        // set height of single entry where collapsed entries sit
        singleEntry.style.height = `${entryBaseHeight + (5 * enterStore.lines.length - 1)}px`;

        // set stack transformations
        ref.style.setProperty(stack1Transform, stackTransform?.stack1 ?? '');
        ref.style.setProperty(stack2Transform, stackTransform?.stack2 ?? '');
        ref.style.setProperty(stack3Transform, stackTransform?.stack3 ?? '');
      }
    }, [summariseEntries, enterStore.lines.length]);
    const slidingEntryPicker = useLotteryEntryPickerSlidingModal();

    return (
      <div
        ref={entriesContainerRef}
        className={clsx(
          styles.entriesContainer,
          collapsed && styles.collapsed,
          summariseEntries && styles.summariseEntries,
        )}
      >
        {enterStore.lines.map((x, i) => (
          <div
            className={clsx(styles.entryWrapper, styles[`stackItem--${i}`])}
            key={i}
            ref={entryRefs[i]}
          >
            <FadeIn onScroll duration={0.3} delay={i * 0.05}>
              <EditableLotteryEntry
                main={x.main}
                bonus={x.bonus}
                lineNumber={i + 1}
                showTapToChange={i === 0}
                onRemoveClick={
                  i > 0 ? () => enterStore.removeLine(i) : undefined
                }
                onRandomiseClick={() => enterStore.generateLineRandomNumbers(i)}
                onEditClick={() => {
                  setOpenCallout(false);
                  slidingEntryPicker.startEditing(i);
                }}
                variant="V2"
                toggleCollapse={() => setSummariseEntires(!summariseEntries)}
                collapsed={collapsed && i > 0}
                disabled={
                  summariseEntries || enterStore.lines.length === 1
                    ? false
                    : (collapsed && i === 0) ||
                      (collapsed &&
                        !(enterStore.lines.length === 1
                          ? false
                          : collapsed && i === enterStore.lines.length - 1))
                }
                firstItemCollapsed={
                  enterStore.lines.length === 1 ? false : collapsed && i === 0
                }
                stackLength={enterStore.lines.length}
                stackIndex={i}
                ref={i === 0 ? firstEntryRef : null}
                pulse={showPulse}
              />
            </FadeIn>
          </div>
        ))}
        {firstInvalidLineIndex !== -1 && (
          <Callout
            anchorRef={entryRefs[firstInvalidLineIndex]}
            open={openCallout}
            close={() => setOpenCallout(false)}
            content={
              <div
                className={clsx(styles.calloutText, 'daymadeTypographySMedium')}
              >
                {calloutText}
              </div>
            }
            label="Callout prompt"
          />
        )}
        {summariseEntries && (
          <button
            className={clsx('buttonReset', styles.editNumberOverlay)}
            onClick={() => slidingEntryPicker.startEditing(0)}
          />
        )}
        {slidingEntryPicker.render()}
      </div>
    );
  },
);

export default V2EntriesPicker;
